// src/config/apiConfig.ts
import { getLogger } from "../helpers/logger";
import { apiBaseUrl, resolveApiEnvironment } from "./app-config";

const configLogger = getLogger("ApiConfig");

/**
 * Endpoints the worker actually serves. Two were declared here that it does not:
 * `/redeem-beta-code`, which had a full client path and no route and no caller, and
 * `/log-errors`, which nothing read at all — helpers/logger.ts hardcodes its own URL.
 */
export interface ApiEndpoints {
  captureToFile: string;
  captureFromCode: string;
  captureFromZip: string;
  userStatus: (userId: string) => string;
  recordImportUsage: string;
  health: string;
  /**
   * The Chrome extension install link, resolved server-side.
   *
   * Declared here rather than written into the two components that show it, because when it WAS
   * written into them they disagreed — one pointed at go.tofig.design, the other at
   * go.tomake.design, one extension, at most one of them right. The worker answers this with a
   * 302 to the real host, so the destination is correctable without a plugin release.
   */
  chromeExtension: string;
}

export interface ApiTimeouts {
  default: number;
  fetch: number;
  health: number;
  upload: number;
  conversion: number;
  batch: number;
}

export interface ApiConfig {
  baseUrl: string;
  endpoints: ApiEndpoints;
  timeout: ApiTimeouts;
  maxRetries: number;
  retryDelay: number;
  retryMultiplier: number;
  connectionTimeout: number;
  maxConcurrentRequests: number;
}

/**
 * Which deployment the plugin talks to.
 *
 * A constant, and it always was. `FORCE_ENVIRONMENT` was set to `'production'` and read on the
 * first line of `getEnvironment()`, so the ~28 lines of detection beneath it never ran once. That
 * is the harmless half. The detection could not have worked had it run, which is why it is gone
 * rather than switched on:
 *
 *   - `typeof figma !== 'undefined'` is FALSE here. This module is imported only by the UI side —
 *     services, components, `api/index.ts` — and `figma` exists in the sandbox realm, not in the
 *     UI iframe. `src/plugin/` never imports this file.
 *   - the fallback then read `globalThis.process.env.NODE_ENV`, which does not exist in a browser
 *     bundle.
 *
 * Both paths fell through to the same `return 'production'` — under a comment reading "Default to
 * development for safety", which is the opposite of what the line does.
 *
 * Change this one line to point the plugin at a preview deployment or at `wrangler pages dev`.
 * Both are permitted by the manifest's `devAllowedDomains` — they were not when that sentence was
 * first written, so changing the line alone got a blocked fetch. See config/network-access.test.ts.
 */
// Only used for the log line below. The decision itself lives in app-config.ts's `apiBaseUrl`,
// and this reads the same resolver so the two cannot disagree — a log that names one environment
// while the requests go to another is worse than no log.
const ENVIRONMENT = resolveApiEnvironment();

// Base configuration
const baseConfig: ApiConfig = {
  baseUrl: "", // Will be set by environment
  endpoints: {
    captureToFile: "/capture-to-file",
    captureFromCode: "/capture-from-code",
    captureFromZip: "/capture-from-zip",
    userStatus: (userId: string) => `/user/${userId}/status`,
    recordImportUsage: "/record-import-usage",
    health: "/health",
    chromeExtension: "/chrome-extension",
  },
  timeout: {
    default: 90000, // 1.5 minutes for standard operations - reduced from 3 min
    fetch: 120000, // 2 minutes for fetching design data - reduced from 4 min
    health: 15000, // 15 seconds for health checks - reduced from 1 min
    upload: 180000, // 3 minutes for file uploads - reduced from 5 min
    conversion: 240000, // 4 minutes for complex conversions
    batch: 60000, // 1 minute for batch operations
  },
  maxRetries: 3,
  retryDelay: 1000, // Start with 1 second instead of 3
  retryMultiplier: 2.0, // More aggressive backoff
  connectionTimeout: 30000, // 30 seconds for initial connection
  maxConcurrentRequests: 6, // Allow more concurrent requests
};

// Create the final configuration
function createApiConfig(): ApiConfig {
  return { ...baseConfig, baseUrl: apiBaseUrl() };
}

// Export the configuration
export const apiConfig = createApiConfig();

// Helper function to build full URL
export function buildApiUrl(endpoint: string): string {
  return `${apiConfig.baseUrl}${endpoint}`;
}

// Validation function. Not exported — the only caller is the startup check below.
function validateApiConfig(config: ApiConfig): string[] {
  const errors: string[] = [];
  
  if (!config.baseUrl) {
    errors.push("Base URL is required");
  }
  
  if (!config.baseUrl.startsWith('http')) {
    errors.push("Base URL must start with http:// or https://");
  }
  
  if (config.timeout.default <= 0) {
    errors.push("Default timeout must be greater than 0");
  }
  
  if (config.maxRetries < 0) {
    errors.push("Max retries must be 0 or greater");
  }
  
  return errors;
}

// Log configuration on startup (for debugging)
configLogger.info(`API configuration loaded for environment: ${ENVIRONMENT}`, {
  baseUrl: apiConfig.baseUrl,
  maxRetries: apiConfig.maxRetries,
});

const configErrors = validateApiConfig(apiConfig);
if (configErrors.length > 0) {
  configLogger.error("API Configuration errors:", configErrors);
}